'use client';

import React, { useState, useEffect } from 'react';

const SESSION_TYPES = [
  'Event Photography',
  'Family Session',
  'Real Estate',
  'Graduation',
  'Professional Headshots',
  'Personal Portraits',
  'Senior Portraits',
  'Custom Package',
];

const TERMS_SECTIONS = [
  {
    title: 'Booking & Payment',
    body: 'Full payment is required to secure your session date. Refunds are available up to 24 hours before the scheduled shoot time.',
  },
  {
    title: 'Cancellations',
    body: 'Cancellations within 24 hours of the session will be charged the full amount.',
  },
  {
    title: 'Rescheduling',
    body: 'Rescheduling is available up to 24 hours before scheduled shoot.',
  },
  {
    title: 'Image Delivery',
    body: 'You will receive a gallery of edited digital images within 7 days. Exact number depends on the package.',
  },
  {
    title: 'Printing',
    body: 'Request of a physical print order has an additional fee.',
  },
  {
    title: 'Usage',
    body: 'Regal Portraits reserves the right to use any photographs for gallery images, social media advertisements, and example images. Customers will be notified if their photos will be used as such, but are entitled to deny the use of their photos.',
  },
  {
    title: 'Liability',
    body: 'Our liability is limited to the amount paid for the session. We are not responsible for acts of God or circumstances beyond our control.',
  },
  {
    title: 'Agreement',
    body: 'Upon booking of a session, all terms are agreed to by the customer.',
  },
];

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedPackage?: string;
}

export default function BookingModal({ isOpen, onClose, preselectedPackage }: BookingModalProps) {
  const [selectedType, setSelectedType] = useState('');
  const [name, setName] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [details, setDetails] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (preselectedPackage) setSelectedType(preselectedPackage);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, preselectedPackage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedType || !name || !dateTime || !agreedToTerms) return;

    const body = `Name: ${name}\nPackage: ${selectedType}\nDate + Time: ${dateTime}\nDetails: ${details || 'N/A'}`;
    const mailtoUrl = `mailto:zach@regal-portraits.com?subject=Booking%20Inquiry%20-%20${encodeURIComponent(selectedType)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl, '_blank');
    handleClose();
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSelectedType('');
      setName('');
      setDateTime('');
      setDetails('');
      setAgreedToTerms(false);
    }, 300);
  };

  if (!isOpen) return null;

  const canSubmit = !!selectedType && !!name && !!dateTime && agreedToTerms;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
        onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      >
        <div
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          style={{
            background: '#0F0F14',
            border: '1px solid rgba(139,105,20,0.3)',
            boxShadow: '0 0 80px rgba(212,168,67,0.08)',
          }}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-5 right-5 z-10 w-8 h-8 flex items-center justify-center transition-opacity duration-200 hover:opacity-70"
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="#D4A843" strokeWidth="1.5" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="p-8 md:p-12">
            {/* Header */}
            <div className="text-center mb-10">
              <p
                className="text-[9px] font-bold uppercase tracking-[0.5em] mb-3"
                style={{ color: '#8B6914' }}
              >
                Reserve Your Session
              </p>
              <h2
                className="font-display font-bold"
                style={{ fontSize: 'clamp(1.8rem, 5vw, 2.8rem)', color: '#E8E4DC', lineHeight: 1.1 }}
              >
                Book Now
              </h2>
              <p
                className="mt-3 text-sm leading-relaxed max-w-md mx-auto"
                style={{ color: 'rgba(232,228,220,0.4)' }}
              >
                Tell us about your vision and we'll craft the perfect session for you.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label
                  className="block text-[9px] font-bold uppercase tracking-[0.3em] mb-2"
                  style={{ color: 'rgba(139,105,20,0.8)' }}
                >
                  Name
                </label>
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="field-dark"
                  required
                />
              </div>

              {/* Package */}
              <div>
                <label
                  className="block text-[9px] font-bold uppercase tracking-[0.3em] mb-3"
                  style={{ color: 'rgba(139,105,20,0.8)' }}
                >
                  Package
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {SESSION_TYPES.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setSelectedType(type)}
                      className="py-3 px-3 text-xs font-medium transition-all duration-200 text-left"
                      style={{
                        border: `1px solid ${selectedType === type ? '#D4A843' : 'rgba(232,228,220,0.1)'}`,
                        background: selectedType === type ? 'rgba(212,168,67,0.1)' : 'transparent',
                        color: selectedType === type ? '#D4A843' : 'rgba(232,228,220,0.45)',
                        letterSpacing: '0.03em',
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date + Time */}
              <div>
                <label
                  className="block text-[9px] font-bold uppercase tracking-[0.3em] mb-2"
                  style={{ color: 'rgba(139,105,20,0.8)' }}
                >
                  Date + Time
                </label>
                <input
                  type="text"
                  placeholder="e.g. Aug 30, 2026 at 2:00 PM"
                  value={dateTime}
                  onChange={(e) => setDateTime(e.target.value)}
                  className="field-dark"
                  required
                />
              </div>

              {/* Details */}
              <div>
                <label
                  className="block text-[9px] font-bold uppercase tracking-[0.3em] mb-2"
                  style={{ color: 'rgba(139,105,20,0.8)' }}
                >
                  Details (optional)
                </label>
                <textarea
                  placeholder="Describe your vision, location ideas, or any special requests..."
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  rows={3}
                  className="field-dark resize-none"
                  style={{ width: '100%' }}
                />
              </div>

              {/* Terms Checkbox */}
              <div className="flex items-start gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setAgreedToTerms(!agreedToTerms)}
                  className="flex-shrink-0 w-4 h-4 mt-0.5 flex items-center justify-center transition-all duration-200"
                  style={{
                    border: `1px solid ${agreedToTerms ? '#D4A843' : 'rgba(139,105,20,0.5)'}`,
                    background: agreedToTerms ? 'rgba(212,168,67,0.15)' : 'transparent',
                  }}
                  aria-label="Agree to terms and conditions"
                >
                  {agreedToTerms && (
                    <svg viewBox="0 0 12 12" fill="none" className="w-2.5 h-2.5">
                      <path d="M2 6l3 3 5-5" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
                <span className="text-xs leading-relaxed" style={{ color: 'rgba(232,228,220,0.55)' }}>
                  I agree to all{' '}
                  <button
                    type="button"
                    onClick={() => setShowTerms(true)}
                    className="underline underline-offset-2 transition-colors duration-200 cursor-pointer"
                    style={{ color: '#D4A843' }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.color = '#E8C96A')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.color = '#D4A843')}
                  >
                    terms and conditions
                  </button>
                </span>
              </div>

              <button
                type="submit"
                className="btn-gold w-full py-4 mt-2"
                disabled={!canSubmit}
                style={{ opacity: !canSubmit ? 0.4 : 1 }}
              >
                <span>Book Now</span>
              </button>

              <p className="text-center text-xs" style={{ color: 'rgba(232,228,220,0.2)' }}>
                We respond within 24 hours. Custom packages available for all budgets.
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Terms Modal */}
      {showTerms && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
          onClick={() => setShowTerms(false)}
        >
          <div
            className="relative w-full max-w-lg max-h-[80vh] overflow-y-auto p-8"
            style={{ backgroundColor: '#0f0e0c', border: '1px solid rgba(139,105,20,0.3)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowTerms(false)}
              className="absolute top-4 right-4 transition-opacity duration-200 hover:opacity-70"
              style={{ color: 'rgba(232,228,220,0.4)' }}
              aria-label="Close terms"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2
              className="text-lg font-semibold tracking-widest mb-6"
              style={{ color: '#D4A843', fontFamily: 'var(--font-display, serif)' }}
            >
              TERMS & CONDITIONS
            </h2>

            <div className="space-y-5">
              {TERMS_SECTIONS.map(({ title, body }) => (
                <div key={title}>
                  <p className="text-xs font-semibold tracking-widest mb-1" style={{ color: '#D4A843' }}>
                    {title.toUpperCase()}
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(232,228,220,0.7)' }}>
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
